import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom'
import { useState, useEffect } from 'react'
import { useAuthStore } from '../stores/authStore'
import {
  LayoutDashboard, AlertTriangle, Shield, Key, Brain, BarChart3,
  CheckSquare, ChevronLeft, ChevronRight, LogOut, Menu,
  Search, ClipboardCheck, Award, Wrench, Settings, Users, Building2,
  Bell, ChevronDown, ChevronUp, HardHat, Bot, Briefcase, Plug, ClipboardList, UserCheck, LineChart,
  CreditCard, ShieldCheck, Globe, ShieldAlert, Clock, Link2,
} from 'lucide-react'
import api from '../services/api'
import { useNavPermissions } from '../hooks/useNavPermissions'
import TrialBanner from './TrialBanner'
import DemoBanner from './DemoBanner'
import UpdateNotification from './UpdateNotification'

const ADMIN_ROLES = ['tenant_admin', 'corp_safety']

type NavItem = { to: string; icon: any; label: string; navKey: string }
type NavGroup = { heading: string; items: NavItem[] }

const mainNavGroups: NavGroup[] = [
  {
    heading: '',
    items: [
      { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard', navKey: 'dashboard' },
    ],
  },
  {
    heading: 'Safety',
    items: [
      { to: '/incidents',          icon: AlertTriangle,  label: 'Incidents',          navKey: 'incidents' },
      { to: '/incidents/register', icon: ClipboardList,  label: 'Register of Injuries', navKey: 'incidents-register' },
      { to: '/investigations',     icon: Search,         label: 'Investigations',     navKey: 'investigations' },
      { to: '/psychosocial',       icon: Brain,          label: 'Psychosocial',       navKey: 'psychosocial' },
      { to: '/lone-worker',        icon: ShieldCheck,    label: 'Lone Worker',        navKey: 'lone-worker' },
    ],
  },
  {
    heading: 'Operations',
    items: [
      { to: '/permits',       icon: Key,            label: 'Permits to Work', navKey: 'permits' },
      { to: '/audits',        icon: ClipboardCheck, label: 'Audits',          navKey: 'audits' },
      { to: '/risk-register', icon: ShieldAlert,    label: 'Risk Register',   navKey: 'risk-register' },
      { to: '/assets',        icon: Wrench,         label: 'Assets',          navKey: 'assets' },
    ],
  },
  {
    heading: 'Workforce',
    items: [
      { to: '/competencies', icon: Award,     label: 'Competencies', navKey: 'competencies' },
      { to: '/workforce',    icon: UserCheck, label: 'Workforce',    navKey: 'workforce' },
      { to: '/contractors',  icon: HardHat,   label: 'Contractors',  navKey: 'contractors' },
    ],
  },
  {
    heading: 'Compliance',
    items: [
      { to: '/workers-comp',       icon: Shield,      label: 'Workers Comp',       navKey: 'workers-comp' },
      { to: '/corrective-actions', icon: CheckSquare, label: 'Corrective Actions', navKey: 'corrective-actions' },
    ],
  },
  {
    heading: 'Reporting',
    items: [
      { to: '/metrics',                    icon: BarChart3, label: 'Metrics',           navKey: 'metrics' },
      { to: '/analytics',                  icon: LineChart, label: 'Analytics',         navKey: 'analytics' },
      { to: '/settings/scheduled-reports', icon: Clock,     label: 'Scheduled Reports', navKey: 'scheduled-reports' },
    ],
  },
]

const settingsNav = [
  { to: '/settings/users', icon: Users, label: 'Users', roles: ['tenant_admin', 'corp_safety'] },
  { to: '/settings/roles', icon: Shield, label: 'Roles & Permissions', roles: ['tenant_admin', 'corp_safety'] },
  { to: '/settings/role-nav', icon: LayoutDashboard, label: 'Role Navigation', roles: ['tenant_admin'] },
  { to: '/settings/external-access', icon: Globe, label: 'External Access', roles: ['tenant_admin', 'corp_safety'] },
  { to: '/settings/relationships', icon: Link2, label: 'Cross-Tenant Sharing', roles: ['tenant_admin', 'corp_safety', 'ops_safety'] },
  { to: '/settings/onboarding', icon: ClipboardList, label: 'Onboarding Templates', roles: ['tenant_admin', 'corp_safety'] },
  { to: '/settings/admin', icon: Building2, label: 'Organisation', roles: ['tenant_admin'] },
  { to: '/settings/contracts', icon: Briefcase, label: 'Client Contracts', roles: ['tenant_admin', 'corp_safety'] },
  { to: '/settings/integrations', icon: Plug, label: 'Integrations', roles: ['tenant_admin', 'corp_safety'] },
  { to: '/notifications', icon: Bell, label: 'Notifications', roles: ['tenant_admin', 'corp_safety', 'ops_safety'] },
  { to: '/settings/simulation', icon: Bot, label: 'Simulation', roles: ['tenant_admin', 'corp_safety'] },
  { to: '/settings/billing', icon: CreditCard, label: 'Billing', roles: ['tenant_admin'] },
]

function userInitials(user: any): string {
  if (!user) return '?'
  const f = user.firstName?.[0] || ''
  const l = user.lastName?.[0] || ''
  return (f + l).toUpperCase() || user.email?.[0]?.toUpperCase() || '?'
}

export default function Layout() {
  const [collapsed, setCollapsed] = useState(false)
  const [mobileOpen, setMobileOpen] = useState(false)
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [trialDaysLeft, setTrialDaysLeft] = useState<number | null>(null)
  const { user, logout } = useAuthStore()
  const navigate = useNavigate()
  const location = useLocation()
  const { can } = useNavPermissions()

  const isSettingsActive = location.pathname.startsWith('/settings')

  const filteredNavGroups = mainNavGroups
    .map(group => ({ ...group, items: group.items.filter(item => can(item.navKey)) }))
    .filter(group => group.items.length > 0)

  useEffect(() => {
    if (user?.role !== 'super_admin') {
      api.get('/billing').then(r => {
        if (r.data.is_trial) setTrialDaysLeft(r.data.trial_days_left ?? null)
      }).catch(() => {})
    }
  }, [])

  const handleLogout = async () => {
    try { await api.post('/auth/logout') } catch {}
    logout()
    navigate('/login')
  }

  const canSeeSettings = ADMIN_ROLES.includes(user?.role || '')

  // Shared nav item style helpers
  const navItem = (isActive: boolean) => ({
    background: isActive ? 'var(--sidebar-active-bg)' : 'transparent',
    color: isActive ? 'var(--sidebar-active-text)' : 'var(--sidebar-text)',
    borderLeft: isActive ? '3px solid var(--sidebar-active-text)' : '3px solid transparent',
  })

  const Sidebar = () => (
    <aside
      className={`flex flex-col h-full transition-all duration-250 ${collapsed ? 'w-16' : 'w-60'}`}
      style={{ background: 'var(--sidebar-bg)', borderRight: '1px solid var(--sidebar-border)' }}
    >
      {/* Logo */}
      <div
        className="flex items-center gap-2.5 px-3 py-3"
        style={{ borderBottom: '1px solid var(--sidebar-border)', minHeight: 64 }}
      >
        <div
          className="flex-shrink-0 flex items-center justify-center rounded-lg"
          style={{ width: 36, height: 36, background: 'rgba(0,196,161,0.15)' }}
        >
          <Shield size={18} style={{ color: '#00C4A1' }} />
        </div>
        {!collapsed && (
          <div className="min-w-0">
            <div className="font-extrabold text-sm leading-tight tracking-tight text-white">
              hseq
            </div>
            <div className="text-xs font-medium leading-tight" style={{ color: 'var(--sidebar-heading)', letterSpacing: '0.04em' }}>
              intelligence
            </div>
          </div>
        )}
      </div>

      {/* Nav */}
      <nav className="flex-1 py-3 overflow-y-auto" style={{ scrollbarWidth: 'none' }}>
        {filteredNavGroups.map(({ heading, items }) => (
          <div key={heading || '__top'} className={heading ? 'mt-1' : ''}>
            {heading && !collapsed && (
              <div style={{
                fontSize: 10,
                fontWeight: 700,
                color: 'var(--sidebar-heading)',
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
                padding: '10px 20px 4px',
              }}>
                {heading}
              </div>
            )}
            {heading && collapsed && <div style={{ height: 8 }} />}
            {items.map(({ to, icon: Icon, label }) => (
              <NavLink
                key={to}
                to={to}
                title={collapsed ? label : undefined}
                className="flex items-center gap-3 mx-2 mb-0.5 rounded-lg text-sm font-medium transition-all"
                style={({ isActive }) => ({
                  ...navItem(isActive),
                  padding: collapsed ? '0.55rem 0' : '0.55rem 1rem',
                  paddingLeft: collapsed ? 0 : undefined,
                  justifyContent: collapsed ? 'center' : undefined,
                })}
                onMouseEnter={e => {
                  const el = e.currentTarget
                  if (!el.style.color.includes('C4A1')) {
                    el.style.background = 'var(--sidebar-hover)'
                  }
                }}
                onMouseLeave={e => {
                  const el = e.currentTarget
                  if (!el.style.color.includes('C4A1')) {
                    el.style.background = 'transparent'
                  }
                }}
              >
                <Icon size={17} className="flex-shrink-0" style={{ minWidth: 17 }} />
                {!collapsed && <span>{label}</span>}
              </NavLink>
            ))}
          </div>
        ))}

        {/* Settings section */}
        {canSeeSettings && (
          <div className="mt-1">
            {!collapsed ? (
              <>
                <button
                  onClick={() => setSettingsOpen(o => !o)}
                  className="flex items-center gap-3 mx-2 rounded-lg text-sm font-medium transition-all w-[calc(100%-16px)]"
                  style={{
                    padding: '0.55rem 1rem',
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    color: isSettingsActive ? 'var(--sidebar-active-text)' : 'var(--sidebar-text)',
                    borderLeft: isSettingsActive ? '3px solid var(--sidebar-active-text)' : '3px solid transparent',
                  }}
                >
                  <Settings size={17} className="flex-shrink-0" />
                  <span className="flex-1 text-left">Settings</span>
                  {settingsOpen || isSettingsActive
                    ? <ChevronUp size={13} />
                    : <ChevronDown size={13} />}
                </button>

                {(settingsOpen || isSettingsActive) && (
                  <div className="ml-4">
                    {settingsNav
                      .filter(item => !item.roles || item.roles.includes(user?.role || ''))
                      .map(({ to, icon: Icon, label }) => (
                        <NavLink
                          key={to}
                          to={to}
                          className="flex items-center gap-3 mx-2 mb-0.5 rounded-lg text-sm font-medium transition-all"
                          style={({ isActive }) => ({
                            ...navItem(isActive),
                            padding: '0.45rem 0.875rem',
                          })}
                        >
                          <Icon size={14} className="flex-shrink-0" />
                          <span>{label}</span>
                        </NavLink>
                      ))}
                  </div>
                )}
              </>
            ) : (
              <NavLink
                to="/settings/admin"
                title="Settings"
                className="flex items-center justify-center mx-2 mb-0.5 rounded-lg text-sm font-medium transition-all"
                style={({ isActive }) => ({ ...navItem(isActive), padding: '0.55rem 0' })}
              >
                <Settings size={17} />
              </NavLink>
            )}
          </div>
        )}
      </nav>

      {/* User profile */}
      <div style={{ borderTop: '1px solid var(--sidebar-border)', padding: '12px' }}>
        <div className="flex items-center gap-2.5">
          <div
            className="flex-shrink-0 flex items-center justify-center rounded-full text-xs font-bold"
            style={{ width: 32, height: 32, background: 'var(--accent)', color: '#0D1B3E', fontSize: 12 }}
          >
            {userInitials(user)}
          </div>
          {!collapsed && (
            <div className="flex-1 min-w-0">
              <div className="text-xs font-semibold truncate text-white">
                {user?.firstName} {user?.lastName}
              </div>
              <div className="text-xs truncate capitalize" style={{ color: 'var(--sidebar-heading)' }}>
                {user?.role?.replace(/_/g, ' ')}
              </div>
            </div>
          )}
          {!collapsed && (
            <button
              onClick={handleLogout}
              title="Log out"
              className="flex-shrink-0 rounded-lg p-1.5 transition-all"
              style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.4)' }}
              onMouseEnter={e => (e.currentTarget.style.color = '#ff6b6b')}
              onMouseLeave={e => (e.currentTarget.style.color = 'rgba(255,255,255,0.4)')}
            >
              <LogOut size={15} />
            </button>
          )}
        </div>
        {collapsed && (
          <button
            onClick={handleLogout}
            title="Log out"
            className="flex items-center justify-center w-full mt-2 rounded-lg py-1.5 transition-all"
            style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.4)' }}
          >
            <LogOut size={15} />
          </button>
        )}
      </div>
    </aside>
  )

  return (
    <div className="flex h-screen overflow-hidden" style={{ background: 'var(--bg)' }}>
      {/* Desktop sidebar */}
      <div className="hidden md:flex flex-col relative">
        <Sidebar />
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="absolute -right-3 top-20 w-6 h-6 rounded-full flex items-center justify-center bg-white shadow-md"
          style={{ border: '1px solid var(--border)', color: 'var(--text-secondary)', zIndex: 10 }}
        >
          {collapsed ? <ChevronRight size={11} /> : <ChevronLeft size={11} />}
        </button>
      </div>

      {/* Mobile sidebar overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          <div className="absolute inset-0 bg-black/60" onClick={() => setMobileOpen(false)} />
          <div className="absolute left-0 top-0 h-full w-60">
            <Sidebar />
          </div>
        </div>
      )}

      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Mobile header */}
        <div
          className="md:hidden flex items-center gap-3 px-4 py-3 bg-white border-b"
          style={{ borderColor: 'var(--border)' }}
        >
          <button onClick={() => setMobileOpen(true)}>
            <Menu size={20} style={{ color: 'var(--text-secondary)' }} />
          </button>
          <div
            className="flex items-center justify-center rounded-lg"
            style={{ width: 28, height: 28, background: 'rgba(0,196,161,0.15)' }}
          >
            <Shield size={14} style={{ color: '#00C4A1' }} />
          </div>
          <span className="font-extrabold text-sm tracking-tight" style={{ color: 'var(--brand-navy)' }}>
            hseq <span className="font-medium" style={{ color: 'var(--text-muted)' }}>intelligence</span>
          </span>
        </div>

        <DemoBanner />
        <TrialBanner daysLeft={trialDaysLeft} />
        <UpdateNotification />
        <main className="flex-1 overflow-y-auto p-6">
          <Outlet />
        </main>
      </div>
    </div>
  )
}
